import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentApp } from '../config/firebaseManager';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { Box, Text, VStack, Alert, AlertIcon, AlertTitle, AlertDescription, Badge, Flex } from '@chakra-ui/react';

const FirestoreTest: React.FC = () => {
  const { currentEnvironment } = useEnvironment();
  const [testResults, setTestResults] = useState<{
    users: { success: boolean; error?: string; count?: number };
    bookings: { success: boolean; error?: string; count?: number };
    payments: { success: boolean; error?: string; count?: number };
    technicians: { success: boolean; error?: string; count?: number };
  }>({
    users: { success: false },
    bookings: { success: false },
    payments: { success: false },
    technicians: { success: false },
  });

  const testCollection = async (collectionName: string) => {
    try {
      console.log(`Testing collection: ${collectionName}`);
      const q = query(collection(db, collectionName), limit(1));
      const querySnapshot = await getDocs(q);
      console.log(`${collectionName} test successful, count: ${querySnapshot.size}`);
      return { success: true, count: querySnapshot.size };
    } catch (error) {
      console.error(`Error testing ${collectionName}:`, error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  useEffect(() => {
    const runTests = async () => {
      console.log('Starting Firestore connection tests...');
      
      const usersResult = await testCollection('users');
      const bookingsResult = await testCollection('bookings');
      const paymentsResult = await testCollection('paymentIntents');
      // Note: Technicians are stored in users collection with role='technician'
      const techniciansResult = await testCollection('users');

      setTestResults({
        users: usersResult,
        bookings: bookingsResult,
        payments: paymentsResult,
        technicians: techniciansResult,
      });
    };

    runTests();
  }, [currentEnvironment]);

  const allSuccessful = Object.values(testResults).every(result => result.success);
  const hasErrors = Object.values(testResults).some(result => !result.success);

  return (
    <Box p={6}>
      <Flex alignItems="center" mb={4} gap={2} flexWrap="wrap">
        <Text 
          fontSize={{ base: 'lg', md: 'xl' }}
          fontWeight="bold"
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
          flex="1"
          minW="200px"
        >
          Firestore Connection Test
        </Text>
        <Badge colorScheme={currentEnvironment.name === 'production' ? 'red' : currentEnvironment.name === 'preview' ? 'orange' : 'green'}>
          {currentEnvironment.displayName.toUpperCase()}
        </Badge>
      </Flex>

      {allSuccessful && (
        <Alert status="success" mb={4}>
          <AlertIcon />
          <AlertTitle>All Collections Accessible!</AlertTitle>
          <AlertDescription>
            Firestore connection is working properly for all collections.
          </AlertDescription>
        </Alert>
      )}

      {hasErrors && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <AlertTitle>Firestore Access Issues Detected</AlertTitle>
          <AlertDescription>
            Some collections are not accessible. Check the details below.
          </AlertDescription>
        </Alert>
      )}

      <VStack spacing={3} align="stretch">
        {Object.entries(testResults).map(([collectionName, result]) => (
          <Box
            key={collectionName}
            p={3}
            border="1px"
            borderColor={result.success ? 'green.200' : 'red.200'}
            borderRadius="md"
            bg={result.success ? 'green.50' : 'red.50'}
          >
            <Text fontWeight="bold" color={result.success ? 'green.700' : 'red.700'}>
              {collectionName.charAt(0).toUpperCase() + collectionName.slice(1)} Collection
            </Text>
            {result.success ? (
              <Text color="green.600">
                ✅ Accessible ({result.count} documents found)
              </Text>
            ) : (
              <Text color="red.600">
                ❌ Error: {result.error}
              </Text>
            )}
          </Box>
        ))}
      </VStack>

      <Box mt={6} p={4} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" fontWeight="bold" mb={2}>
          Debug Information:
        </Text>
        <Text fontSize="xs" fontFamily="mono">
          <strong>Environment:</strong> {currentEnvironment.displayName}
        </Text>
        <Text fontSize="xs" fontFamily="mono">
          <strong>Project ID:</strong> {currentEnvironment.firebase.projectId}
        </Text>
        <Text fontSize="xs" fontFamily="mono">
          <strong>Auth Domain:</strong> {currentEnvironment.firebase.authDomain}
        </Text>
        <Text fontSize="xs" fontFamily="mono">
          <strong>API Key:</strong> {currentEnvironment.firebase.apiKey ? 'Set' : 'Not Set'}
        </Text>
      </Box>
    </Box>
  );
};

export default FirestoreTest; 